/**
 * Google Sign-In Button Component
 * Reusable component for Google OAuth across all login pages
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Google keeps ONE sign-in callback per page (the last initialize wins), so
// every button re-initializes with its own on mount; One Tap in App.js runs
// only on storefront pages and never takes a back-office click.
let googleCallbackFn = null;

export default function GoogleSignInButton({ 
  onSuccess, 
  onError, 
  className = '',
  showOneTap = false, // Disabled by default to reduce warnings
  buttonText = 'continue_with'
}) {
  const googleButtonRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const instanceId = useRef(`gsi_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  const handleCredentialResponse = useCallback(async (response) => {
    setLoading(true);
    try {
      if (onSuccess) {
        await onSuccess(response.credential);
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  }, [onSuccess, onError]);

  // Update the global callback when our callback changes
  useEffect(() => {
    googleCallbackFn = handleCredentialResponse;
  }, [handleCredentialResponse]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.warn('Google Client ID not configured');
      return;
    }

    const initializeGoogle = () => {
      if (!window.google?.accounts?.id) return;
      
      window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => {
            // Use the latest callback function
            if (googleCallbackFn) {
              googleCallbackFn(response);
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true,
          use_fedcm_for_prompt: true,
        });

      // Render button for this instance
      if (googleButtonRef.current) {
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          width: 320,
          text: buttonText,
          shape: 'rectangular',
        });
      }

      setScriptLoaded(true);
    };

    // Load script if not already loaded
    if (!document.getElementById('google-identity-script')) {
      const script = document.createElement('script');
      script.id = 'google-identity-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.body.appendChild(script);
    } else if (window.google?.accounts?.id) {
      initializeGoogle();
    }

    // Cleanup - don't cancel on unmount as other instances may be using it
    return () => {
      // Just clear our reference
    };
  }, [buttonText]);

  if (!GOOGLE_CLIENT_ID) {
    return null;
  }

  if (loading) {
    return (
      <Button variant="outline" className={`w-full h-12 ${className}`} disabled>
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Signing in with Google...
      </Button>
    );
  }

  return (
    <div 
      ref={googleButtonRef} 
      className={`w-full flex justify-center [&>div]:w-full ${className}`}
      style={{ minHeight: '44px' }}
      data-testid="google-signin-button"
    />
  );
}
